import React, { useState, useEffect } from 'react';

export function safeClayImageUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  try { const url = new URL(value); return url.protocol === 'https:' ? url.href : undefined; } catch { return undefined; }
}

interface Props {
  hasTraits: boolean;
  membershipType?: string | null;
  canEquip: boolean;
  imageUrl: unknown;
  busy: boolean;
  expanded: boolean;
  onChoose: () => void;
  onToggle: () => void;
}

export default function AssignedClayChoice({ hasTraits, membershipType, canEquip, imageUrl, busy, expanded, onChoose, onToggle }: Props) {
  const [imageFailed, setImageFailed] = useState(false);
  const src = safeClayImageUrl(imageUrl);
  // Only a confirmed free tier should see an upsell; missing profile data is not a free-tier signal.
  const isAssociateMember = membershipType?.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ') === 'associate member';
  useEffect(() => setImageFailed(false), [src]);
  if (!hasTraits) return (
    <a href="https://members.intersectmbo.org/dashboard" target="_blank" rel="noopener noreferrer"
      className="mb-6 block rounded-xl border border-purple-400 bg-purple-950/50 p-4 text-center font-semibold text-white hover:bg-purple-900">
      {isAssociateMember ? 'Want a custom avatar? Become a member!' : 'Looks like you haven’t made a custom avatar yet!'} <span aria-hidden="true">↗</span>
      {!isAssociateMember && <span className="mt-1 block text-sm font-normal text-purple-200">Create your custom avatar in your member dashboard.</span>}
    </a>
  );
  return (
    <div className="mb-6 text-center">
      <button type="button" onClick={onChoose} disabled={busy || !canEquip}
        className="mx-auto flex w-full max-w-sm flex-col items-center rounded-2xl border-2 border-purple-400 bg-purple-900/40 p-5 text-white shadow-lg transition hover:bg-purple-800/50 disabled:cursor-not-allowed disabled:opacity-50">
        {src && !imageFailed ? <img src={src} alt="Your custom clay avatar" referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)} className="mb-4 h-48 w-48 rounded-xl object-contain" /> :
          <span aria-hidden="true" className="mb-4 flex h-32 w-32 items-center justify-center rounded-full bg-purple-800 text-6xl">👤</span>}
        <span className="text-xl font-bold">Use My Clay Avatar</span>
        <span className="mt-1 text-sm text-purple-200">{canEquip ? 'Your personal Intersect avatar' : 'Your traits are not yet available in this catalog'}</span>
      </button>
      <button type="button" onClick={onToggle} aria-expanded={expanded} aria-controls="default-avatar-options"
        className="mt-4 text-sm font-medium text-gray-200 underline hover:text-white">
        {expanded ? 'Hide default avatars' : 'Choose from 10 default avatars'}
      </button>
    </div>
  );
}
